/**
 * Round 3 (2026-05-18) — FIX 2 (#915): account-menu confirm-gate state.
 *
 * The bottom-left avatar used to sign the user out on a single stray click
 * (accidental logout loses their place). The replacement is a small account
 * popover with an explicit two-step sign-out: open menu → "Sign out" → a
 * confirm step ("Sign out?" / "Yes, sign out" + "Cancel"). A single stray
 * click can NEVER sign the user out.
 *
 * This is the PURE state machine for that gate (no React, no Firebase) so
 * the confirm-gate behaviour is unit-testable under the project's
 * no-Firebase test discipline. The component (AppShell) holds the actual
 * popover + focus; this owns the "can we sign out yet?" logic.
 *
 * States:
 *   'closed'    — menu not shown (avatar at rest)
 *   'open'      — menu shown, identity + "Sign out" action visible
 *   'confirm'   — user clicked "Sign out"; explicit confirm required
 */
export type AccountMenuState = 'closed' | 'open' | 'confirm';

export type AccountMenuEvent =
  | 'toggle' // click the avatar
  | 'requestSignOut' // click "Sign out" in the open menu
  | 'cancel' // click "Cancel" / press Escape / click outside
  | 'confirmSignOut'; // click "Yes, sign out" in the confirm step

export interface AccountMenuTransition {
  next: AccountMenuState;
  /** true ONLY on confirmSignOut from the confirm state — the gate opens. */
  doSignOut: boolean;
}

/**
 * Pure reducer for the account-menu confirm gate. `doSignOut` is true ONLY
 * when the user explicitly confirms from the `confirm` state — so no single
 * click, and no accidental path, ever signs the user out.
 */
export function accountMenuReducer(
  state: AccountMenuState,
  event: AccountMenuEvent,
): AccountMenuTransition {
  switch (event) {
    case 'toggle':
      return { next: state === 'closed' ? 'open' : 'closed', doSignOut: false };
    case 'requestSignOut':
      // Only meaningful from the open menu; advances to the confirm step.
      return {
        next: state === 'open' ? 'confirm' : state,
        doSignOut: false,
      };
    case 'cancel':
      return { next: 'closed', doSignOut: false };
    case 'confirmSignOut':
      // The ONLY path that fires the actual sign-out — and only from
      // the deliberate confirm state.
      return state === 'confirm'
        ? { next: 'closed', doSignOut: true }
        : { next: state, doSignOut: false };
    default:
      return { next: state, doSignOut: false };
  }
}
